import axios from 'axios';
import { getEnv } from './utils/env';
import { isPrerendering, withPrerenderFallback, cachePrerenderData, getPrerenderData } from './utils/prerender/prerenderUtils';
import { generateMockV2Response, generateMockV1Response, mockEdgeCases } from './utils/mockData';

// Development flag for using mock data when backend is unavailable
const USE_MOCK_DATA = process.env.NODE_ENV === 'development' && process.env.REACT_APP_USE_MOCK_DATA === 'true';

// Simple in-memory cache for GET requests
const cache = new Map();
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes cache TTL by default
const SEARCH_CACHE_TTL = 10 * 60 * 1000; // 10 minutes for search results

// Maximum number of entries to store in cache to prevent memory leaks
const MAX_CACHE_SIZE = 50;

// Detect prerendering environment
const isPrerenderEnv = isPrerendering();
const isReactSnap = navigator.userAgent === 'ReactSnap'; // Added for react-snap detection

// Set baseURL dynamically
// In development, use relative URLs to leverage setupProxy.js
// In production or prerender, use the full URLs
const isDevEnv = process.env.NODE_ENV === 'development';

// --- START TEMPORARY CHANGE FOR AWS TESTING ---
const connectToAwsInDev = true; // Enable this to test with AWS in development
const awsServerUrl = 'https://d3d8378t728e1f.cloudfront.net'; // Or your specific API Gateway URL
// --- END TEMPORARY CHANGE FOR AWS TESTING ---

const determinedBaseURL = isPrerenderEnv
  ? getEnv('REACT_APP_API_BASE_URL', 'https://d3d8378t728e1f.cloudfront.net')
  : (isDevEnv && !connectToAwsInDev // MODIFIED LINE
      ? ''
      : (isDevEnv && connectToAwsInDev ? awsServerUrl : getEnv('REACT_APP_API_BASE_URL', 'https://d3d8378t728e1f.cloudfront.net'))); // MODIFIED LINE

// Create axios instance with configuration
const API = axios.create({
  baseURL: determinedBaseURL,
  headers: { 'Content-Type': 'application/json' },
  // Add timeout to prevent long-hanging requests
  timeout: isPrerenderEnv ? 120000 : 10000, // Increased timeout for prerendering to match react-snap
  // Enable credentials if your API requires cookies/auth
  withCredentials: false
});

// Add request interceptor for authentication if needed
API.interceptors.request.use(
  config => {
    // Check for prerender mode first
    if (isPrerenderEnv && config.skipPrerenderFetch) {
      // For requests we want to skip during prerendering
      return {
        ...config,
        adapter: () => Promise.resolve({
          data: config.prerenderFallbackData || null,
          status: 200,
          statusText: 'OK',
          headers: {},
          config,
          prerenderSkipped: true
        })
      };
    }

    // If running in react-snap, mock specific API calls
    // This block replaces the previous smaller one for /api/products/search
    if (isReactSnap && config.method === 'get') {
      const url = config.url || '';
      let mockData; 
      let ruleMatched = false; 
      console.log(`ReactSnap: Intercepted GET ${url} with params: ${JSON.stringify(config.params)}`); // Added detailed logging

      if (url.includes('/api/products/search')) {
          console.log('ReactSnap: Matched /api/products/search'); // Log rule match
          mockData = []; ruleMatched = true;
      } else if (url.includes('/api/products/featured-ids')) {
          console.log('ReactSnap: Matched /api/products/featured-ids'); // Log rule match
          // Provide some mock IDs that can be used by the /api/products mock later
          mockData = { productIds: ["mockId1", "mockId2", "mockId3"] }; ruleMatched = true;
      } else if (url.includes('/api/products/categories')) { 
          console.log('ReactSnap: Matched /api/products/categories');
          let countryParam = '';
          if (config.params && config.params.countries) {
            countryParam = config.params.countries;
          } else if (url.includes('?')) {
            const queryString = url.substring(url.indexOf('?'));
            const searchParams = new URLSearchParams(queryString);
            if (searchParams.has('countries')) {
              countryParam = searchParams.get('countries');
            }
          }
          console.log(`ReactSnap: /api/products/categories - determined country: '${countryParam}'`);

          if (countryParam.toLowerCase() === 'canada') {
            mockData = { categories: ["Electronics", "Books", "Home Goods", "Garden", "Health & Wellness", "Sports & Outdoors"] };
          } else if (countryParam) {
            mockData = { categories: [`Category for ${countryParam} 1`, `Category for ${countryParam} 2`] };
          } else {
            mockData = { categories: ["All Products", "Sales", "New Arrivals", "Popular Picks"] }; // Default if no country
          }
          ruleMatched = true;
      } else if (url.includes('/api/products/subcategories')) { 
          console.log('ReactSnap: Matched /api/products/subcategories'); // Log rule match
          // Example: /api/products/subcategories?category=Electronics&countries=canada
          let categoryParam = '';
          let countrySubParam = '';
          if (config.params) {
            categoryParam = config.params.category || '';
            countrySubParam = config.params.countries || '';
          } else if (url.includes('?')) {
            const queryString = url.substring(url.indexOf('?'));
            const searchParams = new URLSearchParams(queryString);
            categoryParam = searchParams.get('category') || '';
            countrySubParam = searchParams.get('countries') || '';
          }
          console.log(`ReactSnap: /api/products/subcategories - category: '${categoryParam}', country: '${countrySubParam}'`);
          if (categoryParam.toLowerCase() === 'electronics' && countrySubParam.toLowerCase() === 'canada') {
            mockData = { subcategories: ["Smartphones", "Laptops", "Audio Equipment"] };
          } else {
            mockData = { subcategories: ["SubcategoryA", "SubcategoryB"] }; 
          }
          ruleMatched = true;
      } else if (url.includes('/api/products/by-category-subcategory-countries')) {
          console.log('ReactSnap: Matched /api/products/by-category-subcategory-countries');
          mockData = [{_id: "1", name: "Mock Product ByCatSubCat 1"}, {_id: "2", name: "Mock Product ByCatSubCat 2"}]; ruleMatched = true;
      } else if (url.includes('/api/products/by-category-and-countries')) {
          console.log('ReactSnap: Matched /api/products/by-category-and-countries');
          mockData = [{_id: "3", name: "Mock Product ByCatCountry 3"}, {_id: "4", name: "Mock Product ByCatCountry 4"}]; ruleMatched = true;
      } else if (/^\/api\/products\/[^/]+$/.test(url) && !url.includes('search') && !url.includes('categories') && !url.includes('subcategories') && !url.includes('featured-ids') && !url.includes('by-category')) {
          console.log('ReactSnap: Matched single product /api/products/:id');
          const productId = url.substring(url.lastIndexOf('/') + 1);
          mockData = {_id: productId, name: `Mock Product ${productId} Details`, description: "Detailed description here.", price: 55.99, imagePath: "/images/mock-detail.jpg", category: "Electronics", country: "canada"}; ruleMatched = true;
      } else if (url === '/api/products' || (url.startsWith('/api/products?') && !url.includes('search') && !url.includes('categories') && !url.includes('subcategories') && !url.includes('featured-ids') && !url.includes('by-category'))) { 
          console.log('ReactSnap: Matched general /api/products or /api/products?');
          let productsMockData = [];
          let countryQuery = '';

          if (config.params && config.params.countries) {
            countryQuery = config.params.countries;
          } else if (url.includes('?')) {
            const queryString = url.substring(url.indexOf('?'));
            const searchParams = new URLSearchParams(queryString);
            if (searchParams.has('countries')) {
              countryQuery = searchParams.get('countries');
            }
          }
          console.log(`ReactSnap: /api/products (general) - determined country: '${countryQuery}'`);

          if (countryQuery.toLowerCase() === 'canada') {
            productsMockData = [
                {_id: "canProd1", name: "Canadian Product 1 (ReactSnap)", category:"Electronics", country:"canada", price:109.99, imagePath:"/images/can-mock1.jpg", shortDescription: "Top electronic item for Canada.", stock: 10, averageRating: 4.5},
                {_id: "canProd2", name: "Canadian Product 2 (ReactSnap)", category:"Books", country:"canada", price:29.99, imagePath:"/images/can-mock2.jpg", shortDescription: "Best selling book in Canada.", stock: 10, averageRating: 4.5},
                {_id: "canProd3", name: "Canadian Product 3 (ReactSnap)", category:"Home Goods", country:"canada", price:79.99, imagePath:"/images/can-mock3.jpg", shortDescription: "Essential home good for Canadians.", stock: 10, averageRating: 4.5},
                {_id: "canProd4", name: "Canadian Product 4 (ReactSnap)", category:"Garden", country:"canada", price:49.99, imagePath:"/images/can-mock4.jpg", shortDescription: "Perfect for Canadian gardens.", stock: 10, averageRating: 4.5},
                {_id: "canProd5", name: "Canadian Product 5 (ReactSnap)", category:"Health & Wellness", country:"canada", price:39.99, imagePath:"/images/can-mock5.jpg", shortDescription: "Stay healthy in Canada.", stock: 10, averageRating: 4.5}
            ];
          } else if (config.params && config.params._id && Array.isArray(config.params._id)) { // For featured products by IDs
             console.log('ReactSnap: Mocking /api/products for featured IDs:', config.params._id);
             productsMockData = config.params._id.map(id => ({
                _id: id, name: `Featured Mock Product ${id}`, category:"Featured", country:"canada", price:88.88, imagePath:`/images/featured-${id}.jpg`, shortDescription: `Featured item ${id}.`, stock: 10, averageRating: 4.5
             }));
          }
          else { // Default general products
            productsMockData = [
                {_id: "genProd1", name: "General Mock Product 1 (ReactSnap)", category:"Electronics", country:"default", price:99.99, imagePath:"/images/gen-mock1.jpg", shortDescription: "A great electronic gadget.", stock: 10, averageRating: 4.5},
                {_id: "genProd2", name: "General Mock Product 2 (ReactSnap)", category:"Books", country:"default", price:19.99, imagePath:"/images/gen-mock2.jpg", shortDescription: "An interesting book.", stock: 10, averageRating: 4.5}
            ];
          }
          mockData = productsMockData;
          ruleMatched = true;
      } else if (url.includes('/api/loved-products/check/')) {
          console.log('ReactSnap: Matched /api/loved-products/check/'); // Log rule match
          mockData = { isLoved: false }; ruleMatched = true;
      } else if (url.includes('/api/loved-products/user/')) {
          console.log('ReactSnap: Matched /api/loved-products/user/'); // Log rule match
          mockData = []; ruleMatched = true;
      } else {
          console.log(`ReactSnap: No mock rule matched for GET ${url}`); // Log if no rule matched
      }

      if (ruleMatched) {
          const mockDataString = mockData === null ? 'null' : JSON.stringify(mockData);
          console.log(`ReactSnap: Mocking GET ${url}. Returning: ${mockDataString}`);
          return {
              ...config,
              adapter: () => Promise.resolve({
                  data: mockData,
                  status: 200,
                  statusText: 'OK (mocked for ReactSnap)',
                  headers: {},
                  config,
                  isMocked: true // Custom flag
              })
          };
      }
    }
    
    // Dynamically adjust timeout for different request types
    if (config.url?.includes('/search')) {
      config.timeout = isPrerenderEnv ? 45000 : 15000; // Longer timeout for search requests
    }
    // For other requests, ensure they also get the increased prerender timeout if not search
    else if (isPrerenderEnv) {
      config.timeout = Math.max(config.timeout || 0, 120000); // Ensure at least 120s for prerender
    }

    // Check prerender cache first
    if (config.method === 'get' && !config.skipPrerenderCache && isPrerenderEnv) {
      const prerenderCacheKey = `prerender_${config.url}${JSON.stringify(config.params || {})}`;
      const prerenderData = getPrerenderData(prerenderCacheKey);
      
      if (prerenderData) {
        return {
          ...config,
          adapter: () => Promise.resolve({
            data: prerenderData,
            status: 200,
            statusText: 'OK',
            headers: {},
            config,
            prerenderCached: true
          })
        };
      }
    }

    // Check memory cache for GET requests before sending
    if (config.method === 'get' && !config.skipCache && !isPrerenderEnv) {
      const cacheKey = `${config.url}${JSON.stringify(config.params || {})}`;
      const cachedResponse = cache.get(cacheKey);
      
      if (cachedResponse && Date.now() < cachedResponse.expiry) {
        // Return cached response as a resolved promise
        return {
          ...config,
          adapter: () => Promise.resolve({
            data: cachedResponse.data,
            status: 200,
            statusText: 'OK',
            headers: cachedResponse.headers,
            config,
            cached: true
          })
        };
      }
    }
    
    // Add auth tokens here if needed
    // const token = localStorage.getItem('token');
    // if (token) {
    //   config.headers.Authorization = `Bearer ${token}`;
    // }
    return config;
  },
  error => Promise.reject(error)
);

// Add response interceptor for error handling
API.interceptors.response.use(
  response => {
    // Add safety check to ensure API responses are properly formed
    // This prevents HTML responses from being treated as valid API responses
    if (response.data && 
        typeof response.data === 'string' && 
        response.data.trim().startsWith('<!DOCTYPE html>') &&
        (response.config.url.includes('/api/') || 
         response.config.url.includes('/loved-products/'))) {
      // Convert HTML responses for API calls to proper error responses
      console.error('Received HTML response for API call:', response.config.url);
      return Promise.reject({
        response: {
          status: 500,
          data: { error: 'Received HTML instead of JSON. API endpoint may be misconfigured.' }
        }
      });
    }

    // Store successful GET responses in prerender cache when in prerendering mode
    if (response.config.method === 'get' && !response.config.skipPrerenderCache && 
        !response.prerenderCached && isPrerenderEnv) {
      const prerenderCacheKey = `${response.config.url}${JSON.stringify(response.config.params || {})}`;
      cachePrerenderData(prerenderCacheKey, response.data);
    }
    
    // Store successful GET responses in memory cache (for normal browsing)
    if (response.config.method === 'get' && !response.config.skipCache && 
        !response.cached && !isPrerenderEnv) {
      const cacheKey = `${response.config.url}${JSON.stringify(response.config.params || {})}`;
      
      // Determine TTL based on request type (longer for search)
      let ttl = response.config.cacheTTL || CACHE_TTL;
      if (response.config.url?.includes('/search')) {
        ttl = SEARCH_CACHE_TTL;
      }
      
      // Add to cache
      cache.set(cacheKey, {
        data: response.data,
        headers: response.headers,
        expiry: Date.now() + ttl
      });
      
      // Implement LRU-like behavior - remove oldest entries if cache gets too big
      if (cache.size > MAX_CACHE_SIZE) {
        const oldestKey = cache.keys().next().value;
        cache.delete(oldestKey);
      }
    }
    return response;
  },
  async error => {
    // Skip handling for deliberately canceled requests
    if (axios.isCancel(error)) {
      console.info('Request was canceled:', error.message);
      return Promise.reject(error);
    }
    
    const originalRequest = error.config;
    if (!originalRequest) {
      return Promise.reject(error);
    }
    
    // Special handling for prerender environment errors
    if (isPrerenderEnv && originalRequest.prerenderFallbackData) {
      console.warn('Using fallback data for failed request in prerender environment:', originalRequest.url);
      return Promise.resolve({
        data: originalRequest.prerenderFallbackData,
        status: 200,
        statusText: 'OK',
        headers: {},
        config: originalRequest,
        prerenderFallback: true
      });
    }
    
    // Log detailed error information for debugging (only for non-canceled requests)
    console.error('API Error:', {
      url: originalRequest?.url,
      method: originalRequest?.method,
      status: error.response?.status,
      message: error.message,
      name: error.name,
      code: error.code
    });

    // Handle specific error cases
    if (error.code === 'ERR_NETWORK') {
      console.error('Network error detected. Server might be unavailable or CORS issues.');
      
      // More detailed logging for CORS debugging
      if (process.env.NODE_ENV === 'development') {
        console.error(`
          CORS Issue Detected: If you're seeing this in development, check that:
          1. The setupProxy.js is configured correctly
          2. Your API URL is relative (not absolute) in development
          3. Your API server has CORS headers properly configured
          
          Current request URL: ${originalRequest.baseURL || ''}${originalRequest.url || ''}
        `);
      }
      
      // Implement retry logic for network errors
      if (!originalRequest._retry && !originalRequest.url.includes('/search')) {
        originalRequest._retry = true;
        // Add a small delay before retrying
        await new Promise(resolve => setTimeout(resolve, 1000));
        return API(originalRequest);
      }
    }

    // Handle timeout errors with retry - but be careful with search
    if (error.code === 'ECONNABORTED' && originalRequest.timeout) {
      console.error('Request timeout detected.');
      
      // For search requests, we'll be more careful about retrying
      if (originalRequest.url.includes('/search')) {
        // Only retry once and only if it's not a particularly heavy search
        const isHeavySearch = originalRequest.params?.q?.length > 20 || 
                            (originalRequest.params?.page && originalRequest.params.page > 1);
                            
        if (!originalRequest._timeoutRetry && !isHeavySearch) {
          console.log('Retrying search with increased timeout');
          originalRequest._timeoutRetry = true;
          originalRequest.timeout = 20000; // 20 seconds for retry
          return API(originalRequest);
        }
      } 
      // For non-search requests, retry more aggressively
      else if (!originalRequest._timeoutRetry) {
        originalRequest._timeoutRetry = true;
        originalRequest.timeout = originalRequest.timeout * 1.5; // Increase timeout by 50%
        return API(originalRequest);
      }
    }

    return Promise.reject(error);
  }
);

// Development Mock Response Interceptor for Pagination Testing
if (process.env.NODE_ENV === 'development' && process.env.REACT_APP_USE_MOCK_PAGINATION === 'true') {
  // Add response interceptor for mocking pagination data
  API.interceptors.response.use(
    (response) => {
      // Check if this is a products API call
      const isProductsAPI = response.config?.url?.includes('/products') || 
                           response.config?.url?.includes('/api/v2/products');
      
      // Enhanced check: Mock if response failed OR if it's a successful response with very small dataset for testing
      const shouldMockSmallDatasets = process.env.REACT_APP_MOCK_SMALL_DATASETS === 'true';
      const shouldMock = isProductsAPI && (
        !response.data || 
        response.status >= 400 ||
        // Only mock successful responses that have very small datasets (< 5 products) to avoid interfering with real data
        // Since Health & Wellness has 92 real products, we shouldn't mock it
        (shouldMockSmallDatasets && response.data && Array.isArray(response.data) && response.data.length < 5) ||
        (shouldMockSmallDatasets && response.data && response.data.products && Array.isArray(response.data.products) && response.data.products.length < 5)
      );
      
      if (shouldMock) {
        console.log('🔧 [Mock API] Generating mock pagination response for:', response.config.url, 
          'Reason:', !response.data ? 'No data' : 
                    response.status >= 400 ? 'Error status' : 
                    'Very small dataset detected (< 5 products)');
        
        // Generate mock products for testing pagination
        const generateMockProducts = (count, page = 1, category = null) => {
          return Array.from({ length: count }, (_, i) => ({
            _id: `mock-product-${page}-${i + 1}`,
            name: `Mock Product ${((page - 1) * count) + i + 1} ${category ? `(${category})` : ''}`,
            description: `This is a mock product for testing pagination functionality. Page ${page}, Item ${i + 1}${category ? ` in ${category}` : ''}`,
            price: Math.floor(Math.random() * 100) + 10,
            category: category || ['Health & Wellness', 'Food & Beverage', 'Home & Garden'][Math.floor(Math.random() * 3)],
            subcategory: ['Supplements', 'Beverages', 'Tools'][Math.floor(Math.random() * 3)],
            country: 'canada',
            imageUrl: 'https://via.placeholder.com/300x200',
            brand: `Brand ${Math.floor(Math.random() * 10) + 1}`,
            inStock: Math.random() > 0.2,
            isLoved: false
          }));
        };
        
        // Parse pagination params from request
        const url = new URL(response.config.url, 'http://localhost');
        const limit = parseInt(url.searchParams.get('limit')) || 20;
        const page = parseInt(url.searchParams.get('page')) || 1;
        const nextToken = url.searchParams.get('nextToken');
        
        // Extract category from request for category-specific mocking
        const requestCategory = response.config.params?.category || url.searchParams.get('category');
        
        // Calculate pagination
        const totalProducts = 100; // Mock total
        const startIndex = (page - 1) * limit;
        const endIndex = Math.min(startIndex + limit, totalProducts);
        const hasMore = endIndex < totalProducts;
        const currentPageProducts = generateMockProducts(endIndex - startIndex, page, requestCategory);
        
        const mockResponse = {
          data: {
            products: currentPageProducts,
            pagination: {
              hasMore: hasMore,
              nextToken: hasMore ? `token-page-${page + 1}` : null,
              total: totalProducts,
              currentPage: page,
              limit: limit
            }
          },
          status: 200,
          statusText: 'OK (Enhanced Mock)'
        };
        
        console.log('🔧 [Mock API] Generated enhanced response:', {
          productsCount: currentPageProducts.length,
          hasMore,
          page,
          totalProducts,
          category: requestCategory,
          originalDataLength: response.data?.products?.length || response.data?.length || 0
        });
        
        return mockResponse;
      }
      
      return response;
    },
    (error) => {
      // Handle network errors for products API
      const isProductsAPI = error.config?.url?.includes('/products') || 
                           error.config?.url?.includes('/api/v2/products');
      
      if (isProductsAPI && error.code === 'ERR_NETWORK') {
        console.log('🔧 [Mock API] Network error, generating mock response for:', error.config.url);
        
        // Return same mock structure as above
        const generateMockProducts = (count, page = 1) => {
          return Array.from({ length: count }, (_, i) => ({
            _id: `mock-product-${page}-${i + 1}`,
            name: `Mock Product ${((page - 1) * count) + i + 1}`,
            description: `This is a mock product for testing pagination functionality. Page ${page}, Item ${i + 1}`,
            price: Math.floor(Math.random() * 100) + 10,
            category: ['Health & Wellness', 'Food & Beverage', 'Home & Garden'][Math.floor(Math.random() * 3)],
            subcategory: ['Supplements', 'Beverages', 'Tools'][Math.floor(Math.random() * 3)],
            country: 'canada',
            imageUrl: 'https://via.placeholder.com/300x200',
            brand: `Brand ${Math.floor(Math.random() * 10) + 1}`,
            inStock: Math.random() > 0.2,
            isLoved: false
          }));
        };
        
        return Promise.resolve({
          data: {
            products: generateMockProducts(20, 1),
            pagination: {
              hasMore: true,
              nextToken: 'token-page-2',
              total: 100,
              currentPage: 1,
              limit: 20
            }
          },
          status: 200,
          statusText: 'OK (Mock)'
        });
      }
      
      return Promise.reject(error);
    }
  );
}

// Helper methods to control caching
API.clearCache = (urlPattern = null) => {
  if (urlPattern) {
    // Clear specific cache entries that match the pattern
    cache.forEach((value, key) => {
      if (key.includes(urlPattern)) {
        cache.delete(key);
      }
    });
  } else {
    // Clear entire cache
    cache.clear();
  }
};

// Special helper methods for prerendering
API.withPrerenderFallback = (config, fallbackData) => ({
  ...config,
  prerenderFallbackData: fallbackData,
  skipPrerenderFetch: isPrerenderEnv
});

// Fetch with fallback for prerendering
API.fetchWithFallback = async (config, fallbackData) => {
  return withPrerenderFallback(
    () => API(config),
    fallbackData
  );
};

// Existing helpers
API.clearSearchCache = () => {
  API.clearCache('/search');
};

API.skipCache = config => ({
  ...config,
  skipCache: true,
  skipPrerenderCache: true
});

API.withCacheTTL = (config, ttlMs) => ({
  ...config,
  cacheTTL: ttlMs
});

// V2 API Endpoint Helpers
// These functions provide convenient access to V2 paginated endpoints
// with proper error handling and response normalization

/**
 * Fetch products using V2 paginated API
 * @param {Object} params - Query parameters
 * @param {number} params.limit - Items per page (max 200)
 * @param {string} params.nextToken - Token for next page
 * @returns {Promise} API response with normalized pagination
 */
API.getV2Products = (params = {}) => {
  return API.get('/api/v2/products', { params });
};

/**
 * Search products using V2 paginated API
 * @param {Object} params - Query parameters
 * @param {string} params.q - Search query
 * @param {number} params.limit - Items per page (max 200)
 * @param {string} params.nextToken - Token for next page
 * @returns {Promise} API response with normalized pagination
 */
API.searchV2Products = (params = {}) => {
  return API.get('/api/v2/products/search', { params });
};

/**
 * Fetch products by category and countries using V2 paginated API
 * @param {Object} params - Query parameters
 * @param {string} params.category - Product category (required)
 * @param {string} params.countries - Comma-separated country list (required)
 * @param {number} params.limit - Items per page (max 200)
 * @param {string} params.nextToken - Token for next page
 * @returns {Promise} API response with normalized pagination
 */
API.getV2ProductsByCategoryAndCountries = (params = {}) => {
  return API.get('/api/v2/products/by-category-and-countries', { params });
};

/**
 * Fetch products by category, subcategory, and countries using V2 paginated API
 * @param {Object} params - Query parameters
 * @param {string} params.category - Product category (required)
 * @param {string} params.subcategory - Product subcategory (required)
 * @param {string} params.countries - Comma-separated country list (required)
 * @param {number} params.limit - Items per page (max 200)
 * @param {string} params.nextToken - Token for next page
 * @returns {Promise} API response with normalized pagination
 */
API.getV2ProductsByCategorySubcategoryCountries = (params = {}) => {
  return API.get('/api/v2/products/by-category-subcategory-countries', { params });
};

/**
 * Fetch user's loved products using V2 paginated API
 * @param {string} userEmail - User's email address
 * @param {Object} params - Query parameters
 * @param {number} params.limit - Items per page (max 200)
 * @param {string} params.nextToken - Token for next page
 * @param {boolean} params.full - Whether to return full product details
 * @returns {Promise} API response with normalized pagination
 */
API.getV2LovedProducts = (userEmail, params = {}) => {
  return API.get(`/api/v2/loved-products/user/${userEmail}`, { params });
};

/**
 * Generic V2 API fetcher with automatic error handling and fallback
 * @param {string} endpoint - V2 API endpoint
 * @param {Object} params - Query parameters
 * @param {Object} options - Additional options
 * @param {string} options.fallbackEndpoint - V1 endpoint to fallback to
 * @param {boolean} options.enableFallback - Whether to enable V1 fallback
 * @returns {Promise} API response
 */
API.fetchV2WithFallback = async (endpoint, params = {}, options = {}) => {
  const { fallbackEndpoint, enableFallback = true } = options;
  
  try {
    // Try V2 endpoint first
    const response = await API.get(endpoint, { params });
    return response;
  } catch (v2Error) {
    console.warn(`V2 API failed for ${endpoint}:`, v2Error.message);
    
    // Fallback to V1 if enabled and endpoint provided
    if (enableFallback && fallbackEndpoint) {
      console.log(`Falling back to V1 endpoint: ${fallbackEndpoint}`);
      try {
        const fallbackResponse = await API.get(fallbackEndpoint, { params });
        
        // Transform V1 response to match V2 format
        const data = Array.isArray(fallbackResponse.data) 
          ? fallbackResponse.data 
          : fallbackResponse.data.products || [];
        
        return {
          ...fallbackResponse,
          data: {
            products: data,
            pagination: {
              hasMore: false,
              nextToken: null,
              total: data.length
            }
          },
          v1Fallback: true
        };
      } catch (v1Error) {
        console.error(`V1 fallback also failed for ${fallbackEndpoint}:`, v1Error.message);
        throw v2Error; // Throw original V2 error
      }
    }
    
    throw v2Error;
  }
};

/**
 * Clear V2 API specific cache entries
 * @param {string} pattern - Cache pattern to clear (optional)
 */
API.clearV2Cache = (pattern = '/api/v2/') => {
  API.clearCache(pattern);
};

export default API;